import React, { useState } from 'react';

interface TrainingData {
  year: number;
  planned: {
    classes: number;
    students: number;
  };
  total: {
    classes: number;
    students: number;
  };
  internal: {
    classes: number;
    students: number;
  };
  vna: {
    classes: number;
    students: number;
  };
  partner: {
    classes: number;
    students: number;
  };
  projects?: {
    classes: number;
    students: number;
  };
  performance: {
    classesPercent: string;
    studentsPercent: string;
  };
  previousYear?: {
    classesPercent: string;
    studentsPercent: string;
  };
}

const TrainingPlanImplementationReport: React.FC = () => {
  const [selectedYear, setSelectedYear] = useState<string>('all');
  const [exportFormat, setExportFormat] = useState<string>('excel');

  const trainingData: TrainingData[] = [
    {
      year: 2016,
      planned: { classes: 2808, students: 34219 },
      total: { classes: 3034, students: 38492 },
      internal: { classes: 2386, students: 27396 },
      vna: { classes: 128, students: 3500 },
      partner: { classes: 520, students: 7596 },
      performance: { classesPercent: '108%', studentsPercent: '112%' }
    },
    {
      year: 2017,
      planned: { classes: 2768, students: 46441 },
      total: { classes: 4023, students: 53832 },
      internal: { classes: 3229, students: 41163 },
      vna: { classes: 342, students: 5372 },
      partner: { classes: 452, students: 7297 },
      performance: { classesPercent: '145%', studentsPercent: '116%' },
      previousYear: { classesPercent: '133%', studentsPercent: '140%' }
    },
    {
      year: 2018,
      planned: { classes: 4009, students: 46389 },
      total: { classes: 4635, students: 66371 },
      internal: { classes: 3343, students: 48214 },
      vna: { classes: 335, students: 4195 },
      partner: { classes: 957, students: 13962 },
      performance: { classesPercent: '116%', studentsPercent: '143%' },
      previousYear: { classesPercent: '115%', studentsPercent: '123%' }
    },
    {
      year: 2019,
      planned: { classes: 5009, students: 55772 },
      total: { classes: 5405, students: 67954 },
      internal: { classes: 4287, students: 53230 },
      vna: { classes: 270, students: 3517 },
      partner: { classes: 848, students: 11207 },
      performance: { classesPercent: '108%', studentsPercent: '122%' },
      previousYear: { classesPercent: '117%', studentsPercent: '102%' }
    },
    {
      year: 2020,
      planned: { classes: 3611, students: 44153 },
      total: { classes: 4040, students: 42744 },
      internal: { classes: 3349, students: 33260 },
      vna: { classes: 363, students: 4697 },
      partner: { classes: 328, students: 4787 },
      performance: { classesPercent: '112%', studentsPercent: '97%' },
      previousYear: { classesPercent: '75%', studentsPercent: '63%' }
    },
    {
      year: 2021,
      planned: { classes: 2963, students: 46002 },
      total: { classes: 4623, students: 51261 },
      internal: { classes: 3305, students: 35239 },
      vna: { classes: 804, students: 10457 },
      partner: { classes: 514, students: 5565 },
      performance: { classesPercent: '156%', studentsPercent: '111%' },
      previousYear: { classesPercent: '114%', studentsPercent: '120%' }
    },
    {
      year: 2022,
      planned: { classes: 5214, students: 65581 },
      total: { classes: 7610, students: 89806 },
      internal: { classes: 5983, students: 69838 },
      vna: { classes: 723, students: 9311 },
      partner: { classes: 904, students: 10657 },
      performance: { classesPercent: '146%', studentsPercent: '137%' },
      previousYear: { classesPercent: '165%', studentsPercent: '175%' }
    },
    {
      year: 2023,
      planned: { classes: 5595, students: 70555 },
      total: { classes: 8024, students: 86880 },
      internal: { classes: 6579, students: 68833 },
      vna: { classes: 768, students: 9142 },
      partner: { classes: 553, students: 7387 },
      projects: { classes: 124, students: 1518 },
      performance: { classesPercent: '143%', studentsPercent: '123%' },
      previousYear: { classesPercent: '105%', studentsPercent: '97%' }
    },
    {
      year: 2025,
      planned: { classes: 5859, students: 80423 },
      total: { classes: 7595, students: 94873 },
      internal: { classes: 6009, students: 75173 },
      vna: { classes: 589, students: 6540 },
      partner: { classes: 547, students: 6825 },
      projects: { classes: 450, students: 6335 },
      performance: { classesPercent: '130%', studentsPercent: '118%' },
      previousYear: { classesPercent: '95%', studentsPercent: '109%' }
    },
    {
      year: 2025,
      planned: { classes: 6918, students: 63065 },
      total: { classes: 0, students: 0 },
      internal: { classes: 0, students: 0 },
      vna: { classes: 0, students: 0 },
      partner: { classes: 0, students: 0 },
      performance: { classesPercent: '', studentsPercent: '' }
    }
  ];

  const handleExport = () => {
    // Export functionality would be implemented here
    console.log(`Exporting data in ${exportFormat} format`);
  };

  const filteredData = selectedYear === 'all' 
    ? trainingData 
    : trainingData.filter(item => item.year.toString() === selectedYear);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Báo cáo Thực hiện Kế hoạch Đào tạo</h1>
          <p className="text-sm text-gray-600 mt-1">Thống kê tổng hợp hoạt động đào tạo theo năm và đánh giá hiệu quả thực hiện</p>
        </div>
        <div className="flex items-center space-x-4">
          <select 
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="all">Tất cả năm</option>
            {trainingData.map(item => (
              <option key={item.year} value={item.year.toString()}>{item.year}</option>
            ))}
          </select>
          <select 
            value={exportFormat}
            onChange={(e) => setExportFormat(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="excel">Excel</option>
            <option value="pdf">PDF</option>
            <option value="csv">CSV</option>
          </select>
          <button 
            onClick={handleExport}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Xuất báo cáo
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Kế hoạch được giao 2025</h3>
          <div className="space-y-2">
            <p className="text-sm text-gray-600">Số lớp: <span className="font-medium text-gray-900">5,859</span></p>
            <p className="text-sm text-gray-600">Học sinh: <span className="font-medium text-gray-900">80,423</span></p>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Thực hiện 2025</h3>
          <div className="space-y-2">
            <p className="text-sm text-gray-600">Số lớp: <span className="font-medium text-green-600">7,595 (130%)</span></p>
            <p className="text-sm text-gray-600">Học sinh: <span className="font-medium text-green-600">94,873 (118%)</span></p>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Hiệu quả thực hiện</h3>
          <div className="space-y-2">
            <p className="text-sm text-gray-600">Lớp học: <span className="font-medium text-green-600">+30% so với KH</span></p>
            <p className="text-sm text-gray-600">Học sinh: <span className="font-medium text-green-600">+18% so với KH</span></p>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Kế hoạch 2025</h3>
          <div className="space-y-2">
            <p className="text-sm text-gray-600">Số lớp: <span className="font-medium text-blue-600">6,918</span></p>
            <p className="text-sm text-gray-600">Học sinh: <span className="font-medium text-blue-600">63,065</span></p>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            {/* Complex Header */}
            <thead className="bg-blue-50">
              {/* First Header Row */}
              <tr className="border-b border-gray-200">
                <th rowSpan={2} className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase border-r border-gray-200">
                  Năm
                </th>
                <th colSpan={2} className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase border-r border-gray-200">
                  KH được giao
                </th>
                <th colSpan={2} className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase border-r border-gray-200">
                  Tổng số TH trong năm
                </th>
                <th colSpan={2} className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase border-r border-gray-200">
                  TH Đào tạo nội bộ
                </th>
                <th colSpan={2} className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase border-r border-gray-200">
                  TH Đào tạo VNA
                </th>
                <th colSpan={2} className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase border-r border-gray-200">
                  TH Đào tạo đối tác
                </th>
                <th colSpan={2} className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase border-r border-gray-200">
                  TH Các dự án BS
                </th>
                <th colSpan={2} className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase border-r border-gray-200">
                  % thực hiện sv KH năm
                </th>
                <th colSpan={2} className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase">
                  % thực hiện sv cùng kỳ năm trước
                </th>
              </tr>
              {/* Second Header Row */}
              <tr className="border-b border-gray-200">
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-700 border-r border-gray-200">Số lớp</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-700 border-r border-gray-200">SL học sinh</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-700 border-r border-gray-200">Số lớp</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-700 border-r border-gray-200">SL học sinh</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-700 border-r border-gray-200">Số lớp</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-700 border-r border-gray-200">SL học sinh</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-700 border-r border-gray-200">Số lớp</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-700 border-r border-gray-200">L.học sinh</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-700 border-r border-gray-200">Số lớp</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-700 border-r border-gray-200">SL học sinh</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-700 border-r border-gray-200">Số lớp</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-700 border-r border-gray-200">SL học sinh</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-700 border-r border-gray-200">Số lớp</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-700 border-r border-gray-200">SL học sinh</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-700 border-r border-gray-200">Số lớp</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-700">SL học sinh</th>
              </tr>
            </thead>
            
            {/* Table Body */}
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredData.map((row, index) => (
                <tr key={row.year} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-4 py-3 text-center font-medium text-gray-900 border-r border-gray-200">
                    {row.year}
                  </td>
                  <td className="px-3 py-3 text-center text-sm text-gray-900 border-r border-gray-200">
                    {row.planned.classes.toLocaleString()}
                  </td>
                  <td className="px-3 py-3 text-center text-sm text-gray-900 border-r border-gray-200">
                    {row.planned.students.toLocaleString()}
                  </td>
                  <td className="px-3 py-3 text-center text-sm text-gray-900 border-r border-gray-200">
                    {row.total.classes.toLocaleString()}
                  </td>
                  <td className="px-3 py-3 text-center text-sm text-gray-900 border-r border-gray-200">
                    {row.total.students.toLocaleString()}
                  </td>
                  <td className="px-3 py-3 text-center text-sm text-gray-900 border-r border-gray-200">
                    {row.internal.classes.toLocaleString()}
                  </td>
                  <td className="px-3 py-3 text-center text-sm text-gray-900 border-r border-gray-200">
                    {row.internal.students.toLocaleString()}
                  </td>
                  <td className="px-3 py-3 text-center text-sm text-gray-900 border-r border-gray-200">
                    {row.vna.classes.toLocaleString()}
                  </td>
                  <td className="px-3 py-3 text-center text-sm text-gray-900 border-r border-gray-200">
                    {row.vna.students.toLocaleString()}
                  </td>
                  <td className="px-3 py-3 text-center text-sm text-gray-900 border-r border-gray-200">
                    {row.partner.classes.toLocaleString()}
                  </td>
                  <td className="px-3 py-3 text-center text-sm text-gray-900 border-r border-gray-200">
                    {row.partner.students.toLocaleString()}
                  </td>
                  <td className="px-3 py-3 text-center text-sm text-gray-900 border-r border-gray-200">
                    {row.projects?.classes ? row.projects.classes.toLocaleString() : ''}
                  </td>
                  <td className="px-3 py-3 text-center text-sm text-gray-900 border-r border-gray-200">
                    {row.projects?.students ? row.projects.students.toLocaleString() : ''}
                  </td>
                  <td className="px-3 py-3 text-center text-sm font-medium border-r border-gray-200">
                    <span className={`${
                      row.performance.classesPercent.includes('1') && !row.performance.classesPercent.includes('0') 
                        ? 'text-green-600' 
                        : row.performance.classesPercent ? 'text-red-600' : 'text-gray-400'
                    }`}>
                      {row.performance.classesPercent}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-center text-sm font-medium border-r border-gray-200">
                    <span className={`${
                      row.performance.studentsPercent.includes('1') && !row.performance.studentsPercent.includes('0') 
                        ? 'text-green-600' 
                        : row.performance.studentsPercent ? 'text-red-600' : 'text-gray-400'
                    }`}>
                      {row.performance.studentsPercent}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-center text-sm font-medium border-r border-gray-200">
                    <span className={`${
                      row.previousYear?.classesPercent?.includes('1') && !row.previousYear?.classesPercent?.includes('0') 
                        ? 'text-green-600' 
                        : row.previousYear?.classesPercent ? 'text-red-600' : 'text-gray-400'
                    }`}>
                      {row.previousYear?.classesPercent || ''}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-center text-sm font-medium">
                    <span className={`${
                      row.previousYear?.studentsPercent?.includes('1') && !row.previousYear?.studentsPercent?.includes('0') 
                        ? 'text-green-600' 
                        : row.previousYear?.studentsPercent ? 'text-red-600' : 'text-gray-400'
                    }`}>
                      {row.previousYear?.studentsPercent || ''}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TrainingPlanImplementationReport; 